import { BasicIcon, Button, Modal } from "@pickit/pickit-components";
import { RainBonusModalPropsType, RainInputsValuesType } from "./types";
import { useTranslation } from "react-i18next";
import "./RainBonusModal.scss";
import "./i18n";
import RainInputs from "./rainInputs/RainInputsContainer";
import { Form } from "react-final-form";
import classNames from "classnames";
import RainList from "./rainList/RainListContainer";
import { RainDropDownState } from "reducers/types/rainModal";
import { useCallback, useEffect, useMemo } from "react";
import { RAIN_TYPES } from "utils/constants";
import moment from "moment";
import useValidationSchema from "hooks/useValidationSchema";
import { createRainValidationSchema } from "./schemas";
import { formatDDMMYYYY, formatHHMM, isISOTimeAfterNow } from "utils/date";

export const RainBonusModal = ({
  isOpen,
  loading,
  dropdownState,
  actualRegion,
  order,
  rains,
  creationInitialValues,
  resolutionHeightModal,
  formRef,
  rainDirty,
  nextRain,
  timeErrorCode,
  validatingRainTime,
  dirtyRainRef,
  scrollContainerRef,
  blockedTab,
  isDisabledSearch,
  testId,
  onClose,
  toggleDropdown,
  disabledSearch,
  toggleOrder,
  handleSearch,
  createRain,
  setDirtyCreation,
}: RainBonusModalPropsType): JSX.Element => {
  const { t } = useTranslation();

  const dropdownContainerStyles = [
    "rains-container",
    dropdownState === "closed" ? "rains-container-hidden" : "",
    resolutionHeightModal < 600 ? "rains-container-small" : "",
  ].join(" ");

  const minCreationDate = useMemo(
    () => moment().startOf("week").subtract(1, "days"),
    []
  );
  const maxCreationDate = useMemo(() => moment().add(7, "days"), []);

  const limitDates = {
    minDate: minCreationDate,
    maxDate: maxCreationDate,
  };

  const rainList = useMemo<{ [key in RainDropDownState]: JSX.Element | null }>(
    () => ({
      next: (
        <RainList
          rains={rains}
          type={RAIN_TYPES.next}
          dirtyRainRef={dirtyRainRef}
          testId={`${testId}-next-rains`}
        />
      ),
      previous: (
        <RainList
          rains={rains}
          type={RAIN_TYPES.previous}
          testId={`${testId}-previous-rains`}
        />
      ),
      closed: null,
    }),
    [dirtyRainRef, rains, testId]
  );

  const currentOrder = useMemo(
    () => order[dropdownState],
    [dropdownState, order]
  );

  const formFilled = useCallback(
    (values: RainInputsValuesType) =>
      values.rainTime.initial && values.rainTime.end && values.rainDate,
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'bonus-detail');
    } else {
      params.delete('modal');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);

  return (
    <Modal
      width="1190px"
      height={`${resolutionHeightModal}px`}
      isOpen={isOpen}
      onClose={onClose}
      testId={testId}
    >
      <div className="modal-rain-container">
        <div className="container-modal-rain-row">
          <div className="container-modal-rain-col-sm-3">
            <h2 className="modal-rain-title" data-testId={`${testId}-title`}>
              {t("rainBonus:title")}
            </h2>
            <p
              className="modal-rain-subtitle-bonus"
              data-testId={`${testId}-subtitle`}
            >
              {t("rainBonus:subtitle")}
            </p>
          </div>

          <div className="container-modal-rain-col-sm-3">
            <p
              className="modal-rain-state title"
              data-testId={`${testId}-region-title`}
            >
              {t("rainBonus:region")}
            </p>
            <p className="subtitle" data-testId={`${testId}-region`}>
              {actualRegion?.name}
            </p>
          </div>
          <div className="container-modal-rain-col-sm-2">
            <p
              className="modal-rain-state title"
              data-testId={`${testId}-next-rain-title`}
            >
              {nextRain?.startAt && !isISOTimeAfterNow(nextRain?.startAt)
                ? t("rainBonus:currentRain")
                : t("rainBonus:nextRain")}
            </p>
            <p className="subtitle" data-testId={`${testId}-next-rain`}>
              {nextRain?.startAt
                ? `${formatDDMMYYYY(nextRain?.startAt || "")} ${formatHHMM(
                    nextRain?.startAt || ""
                  )} - ${formatHHMM(nextRain?.finishAt || "")}`
                : t("rainBonus:noNextRain")}
            </p>
          </div>
        </div>

        <hr className="modal-rain-separate" id="modal-rain-hr-title" />
        <div className="container-modal-rain-row">
          <div className="container-modal-rain-col">
            <Form
              onSubmit={(values: RainInputsValuesType) => {
                createRain(values);
              }}
              initialValues={creationInitialValues}
              validate={useValidationSchema(
                createRainValidationSchema(timeErrorCode)
              )}
              mutators={{
                setValue: ([field], state) => {
                  setDirtyCreation(!state.formState.pristine);
                  if (field === "rainTime.initial") {
                    const f = state.fields["rainTime.end"];
                    f.touched = true;
                  }
                  disabledSearch(
                    state.formState.values as RainInputsValuesType
                  );
                },
              }}
            >
              {({ handleSubmit, form, values, invalid }) => {
                formRef.current = form;
                return (
                  <form
                    className="container-modal-rain-form"
                    onSubmit={handleSubmit}
                  >
                    <div className="container-modal-rain-form-row">
                      <RainInputs
                        limitDates={limitDates}
                        disabled={loading}
                        form={form}
                        type="creation"
                        testId={`${testId}-new-rain`}
                      />
                      <div className="container-modal-rain-form-col-1">
                        <div className="add-rain-buttons-container">
                          <Button
                            hierarchy="minimal"
                            iconName="search"
                            type="button"
                            label={t("rainBonus:search")}
                            size="small"
                            disabled={loading || isDisabledSearch}
                            onClick={(e) => handleSearch(values, e)}
                            testId={`${testId}-search-button`}
                          />
                          <Button
                            hierarchy="secondary"
                            type="button"
                            label={t("rainBonus:add")}
                            size="small"
                            disabled={
                              loading ||
                              invalid ||
                              !formFilled(values) ||
                              validatingRainTime ||
                              !!form.getState().active
                            }
                            onClick={() => createRain(values)}
                            testId={`${testId}-add-button`}
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                );
              }}
            </Form>
          </div>
        </div>
        <hr className="modal-rain-separate" id="modal-rain-hr-title" />
        <div className="container-modal-rain-row">
          <div className="container-modal-rain-col-2">
            <button
              data-testId={`${testId}-toggle-next`}
              onClick={(e) => toggleDropdown("next", e)}
              type="button"
              className={classNames("rain-dropdown-button", {
                active: dropdownState === "next",
                disabled: blockedTab.next,
              })}
            >
              {t("rainBonus:nextRains")}
              <div className="chevron-container">
                <BasicIcon
                  name={
                    dropdownState === "next" && loading
                      ? "spinner"
                      : "chevron-down"
                  }
                  size="medium"
                />
              </div>
            </button>
          </div>
          <div className="container-modal-rain-col-2">
            <button
              onClick={(e) => toggleDropdown("previous", e)}
              type="button"
              className={classNames("rain-dropdown-button", {
                disabled: rainDirty || blockedTab.previous,
                active: dropdownState === "previous",
              })}
              data-testId={`${testId}-toggle-previous`}
            >
              {t("rainBonus:previousRains")}
              <div className="chevron-container">
                <BasicIcon
                  name={
                    dropdownState === "previous" && loading
                      ? "spinner"
                      : "chevron-down"
                  }
                  size="medium"
                />
              </div>
            </button>
          </div>
          <div className="container-modal-rain-col-3" />
          <div className="container-modal-rain-col-1">
            <Button
              hierarchy="minimal"
              iconName={currentOrder === "ASC" ? "arrow-up" : "arrow-down"}
              type="button"
              label={t("rainBonus:sort")}
              size="small"
              onClick={(e) => toggleOrder(dropdownState, e)}
              disabled={dropdownState === "closed" || loading || rainDirty}
              testId={`${testId}-sort-button`}
            />
          </div>
        </div>
        {!loading && (
          <div className={dropdownContainerStyles} ref={scrollContainerRef}>
            {rainList[dropdownState]}
          </div>
        )}
      </div>
    </Modal>
  );
};
